// Backlog 015-B — client-side TypeScript preflight. Before the (expensive)
// tarball build + Cloud Build round-trip, run `tsc --noEmit` locally against
// each service that ships a tsconfig.json. A production-only type error that
// `cloudgrid dev` skips (Next.js dev mode is lenient) but `next build` enforces
// is then caught in seconds locally instead of after a 2-minute Cloud Build
// that ends in a generic failure. See docs/backlog/015-deploy-observability.md
// §"`cloudgrid dev` vs `next build` parity gap — CLI pre-flight (015-B)".
//
// Pure logic + injected IO: `detectTsServices` decides what to check,
// `runTsPreflight` orchestrates, and the actual `tsc` invocation is injected
// (`TsCheckRunner`) so tests never shell out and non-TS apps pay zero cost.
//
// Voice: §23 — plain, specific, no exclamation marks, no emoji.

import { execa } from 'execa';
import { existsSync } from 'node:fs';
import { join } from 'node:path';

export interface TsServiceTarget {
  service: string;
  dir: string;
}

/** Result of a single `tsc --noEmit` invocation in one service dir. */
export type TsCheckResult =
  | { kind: 'ok' }
  | { kind: 'errors'; output: string }
  | { kind: 'tool-missing' };

/** Runs `tsc --noEmit` in `dir`. Injectable so tests never shell out. */
export type TsCheckRunner = (dir: string) => Promise<TsCheckResult>;

/**
 * Pick the services that warrant a TS preflight: a service whose resolved
 * source dir contains a `tsconfig.json`. `package.json` presence alone is not
 * enough (plenty of plain-JS node services ship one), and a missing tsconfig
 * means there is nothing for `tsc` to read. HTTP-mode cron services have no
 * source dir and are excluded by the caller before this runs.
 */
export function detectTsServices(opts: {
  serviceDirs: TsServiceTarget[];
  fileExists?: (p: string) => boolean;
}): TsServiceTarget[] {
  const exists = opts.fileExists ?? existsSync;
  return opts.serviceDirs.filter((t) => exists(join(t.dir, 'tsconfig.json')));
}

export interface RunTsPreflightOpts {
  targets: TsServiceTarget[];
  runCheck: TsCheckRunner;
  writer: (s: string) => void;
}

export interface TsPreflightFailure {
  service: string;
  output: string;
}

export type TsPreflightOutcome =
  | { action: 'proceed' }
  | { action: 'fail'; failures: TsPreflightFailure[] };

/**
 * Run `tsc --noEmit` against each TS target. A `tool-missing` result (no
 * resolvable tsc in the user's setup) is a soft skip — we note it and proceed
 * rather than block, because Cloud Build still type-checks; failing here would
 * punish users who don't keep a local TypeScript install. Any service with
 * type errors makes the whole preflight fail; the caller aborts before upload
 * so no Cloud Build slot is wasted.
 */
export async function runTsPreflight(
  opts: RunTsPreflightOpts,
): Promise<TsPreflightOutcome> {
  const { targets, runCheck, writer } = opts;
  if (targets.length === 0) return { action: 'proceed' };

  const failures: TsPreflightFailure[] = [];
  for (const t of targets) {
    writer(`Preflight: type-checking ${t.service} (tsc --noEmit)\n`);
    const res = await runCheck(t.dir);
    if (res.kind === 'tool-missing') {
      writer(
        `  No local TypeScript compiler found for ${t.service}; skipping type preflight. Cloud Build will still type-check.\n`,
      );
      continue;
    }
    if (res.kind === 'errors') {
      failures.push({ service: t.service, output: res.output });
    }
  }

  if (failures.length > 0) return { action: 'fail', failures };
  return { action: 'proceed' };
}

/**
 * Default `tsc --noEmit` runner. Resolves tsc through `npx --no-install` so the
 * user's locally installed TypeScript is used (honoring their setup) and no
 * surprise package download happens. A `--version` probe distinguishes "tsc is
 * not installed locally" (soft skip → `tool-missing`) from "tsc ran and found
 * type errors" (hard fail → `errors`).
 */
export const defaultTsCheckRunner: TsCheckRunner = async (dir) => {
  let probe;
  try {
    probe = await execa('npx', ['--no-install', 'tsc', '--version'], {
      cwd: dir,
      reject: false,
    });
  } catch {
    // npx itself unavailable (no Node toolchain on PATH) — don't block.
    return { kind: 'tool-missing' };
  }
  if (probe.exitCode !== 0) return { kind: 'tool-missing' };

  const r = await execa('npx', ['--no-install', 'tsc', '--noEmit'], {
    cwd: dir,
    reject: false,
    all: true,
  });
  if (r.exitCode === 0) return { kind: 'ok' };
  const output = (r.all ?? `${r.stdout}\n${r.stderr}`).trim();
  return {
    kind: 'errors',
    output: output || `tsc exited with code ${r.exitCode}`,
  };
};
